import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  CheckCircle,
  Error,
  Warning,
  Timer,
  Devices,
  Assessment
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

interface StatisticsData {
  totalTests: number;
  todayTests: number;
  weeklyTests: number;
  monthlyTests: number;
  overallSuccessRate: number;
  todaySuccessRate: number;
  environmentStats: {
    [environment: string]: {
      totalTests: number;
      successCount: number;
      failureCount: number;
      successRate: number;
      averageDuration: number;
    };
  };
  testTypeStats: {
    [testType: string]: {
      totalTests: number;
      successCount: number;
      failureCount: number;
      successRate: number;
      averageDuration: number;
    };
  };
  deviceStats: {
    [deviceId: string]: {
      deviceName: string;
      totalTests: number;
      successCount: number;
      failureCount: number;
      successRate: number;
      averageDuration: number;
      lastTestTime?: string;
    };
  };
  failureReasons: {
    reason: string;
    count: number;
    percentage: number;
  }[];
  dailyTrend: {
    date: string;
    totalTests: number;
    successCount: number;
    failureCount: number;
    successRate: number;
    averageDuration: number;
  }[];
  durationDistribution: {
    range: string;
    count: number;
    percentage: number;
  }[];
  lastUpdated: string;
}

interface StatisticsChartsProps {
  data: StatisticsData;
  loading?: boolean;
}

const StatisticsCharts: React.FC<StatisticsChartsProps> = ({ data, loading }) => {
  const { t } = useTranslation();

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <Typography>Loading statistics...</Typography>
      </Box>
    );
  }

  const formatDuration = (ms: number) => {
    const minutes = Math.floor(ms / 60000);
    const seconds = Math.floor((ms % 60000) / 1000);
    return `${minutes}m ${seconds}s`;
  };

  const getSuccessRateColor = (rate: number) => {
    if (rate >= 90) return 'success';
    if (rate >= 70) return 'warning';
    return 'error';
  };

  const getTrendIcon = (current: number, previous: number) => {
    if (current > previous) return <TrendingUp color="success" />;
    if (current < previous) return <TrendingDown color="error" />;
    return <TrendingUp color="disabled" />;
  };

  return (
    <Box>
      {/* 核心指标卡片 */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    {t('statistics.totalTests')}
                  </Typography>
                  <Typography variant="h4" color="primary">
                    {data.totalTests}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {t('statistics.today')}: {data.todayTests}
                  </Typography>
                </Box>
                <Assessment color="primary" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    {t('statistics.overallSuccessRate')}
                  </Typography>
                  <Typography variant="h4" color={getSuccessRateColor(data.overallSuccessRate)}>
                    {data.overallSuccessRate.toFixed(1)}%
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {t('statistics.today')}: {data.todaySuccessRate.toFixed(1)}%
                  </Typography>
                </Box>
                <CheckCircle color={getSuccessRateColor(data.overallSuccessRate)} sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    {t('statistics.weeklyTests')}
                  </Typography>
                  <Typography variant="h4" color="info.main">
                    {data.weeklyTests}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {t('statistics.thisWeek')}
                  </Typography>
                </Box>
                <Timer color="info" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    {t('statistics.activeDevices')}
                  </Typography>
                  <Typography variant="h4" color="secondary.main">
                    {Object.keys(data.deviceStats).length}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {t('statistics.devicesTested')}
                  </Typography>
                </Box>
                <Devices color="secondary" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* 环境统计 */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                📊 {t('statistics.environmentStats')}
              </Typography>
              <Box>
                {Object.entries(data.environmentStats).map(([env, stats]) => (
                  <Box key={env} sx={{ mb: 2 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                      <Typography variant="subtitle1" sx={{ textTransform: 'capitalize' }}>
                        {env}
                      </Typography>
                      <Chip 
                        label={`${stats.successRate.toFixed(1)}%`}
                        color={getSuccessRateColor(stats.successRate)}
                        size="small"
                      />
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={stats.successRate}
                      color={getSuccessRateColor(stats.successRate)}
                      sx={{ height: 8, borderRadius: 4 }}
                    />
                    <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>
                      {stats.totalTests} tests • Avg: {formatDuration(stats.averageDuration)}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* 测试类型统计 */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                🧪 {t('statistics.testTypeStats')}
              </Typography>
              <Box>
                {Object.entries(data.testTypeStats).map(([type, stats]) => (
                  <Box key={type} sx={{ mb: 2 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                      <Typography variant="subtitle1">
                        {type.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      </Typography>
                      <Chip 
                        label={`${stats.successRate.toFixed(1)}%`}
                        color={getSuccessRateColor(stats.successRate)}
                        size="small"
                      />
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={stats.successRate}
                      color={getSuccessRateColor(stats.successRate)}
                      sx={{ height: 8, borderRadius: 4 }}
                    />
                    <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>
                      {stats.totalTests} tests • Avg: {formatDuration(stats.averageDuration)}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* 设备性能统计 */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                📱 {t('statistics.devicePerformance')}
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('statistics.device')}</TableCell>
                      <TableCell align="right">{t('statistics.tests')}</TableCell>
                      <TableCell align="right">{t('statistics.successRate')}</TableCell>
                      <TableCell align="right">{t('statistics.avgDuration')}</TableCell>
                      <TableCell align="right">{t('statistics.lastTest')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {Object.entries(data.deviceStats).map(([deviceId, stats]) => (
                      <TableRow key={deviceId}>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {stats.deviceName}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {deviceId}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">{stats.totalTests}</TableCell>
                        <TableCell align="right">
                          <Chip 
                            label={`${stats.successRate.toFixed(1)}%`}
                            color={getSuccessRateColor(stats.successRate)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="right">
                          {formatDuration(stats.averageDuration)}
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" color="textSecondary">
                            {stats.lastTestTime ? 
                              new Date(stats.lastTestTime).toLocaleDateString() : 
                              'Never'
                            }
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* 失败原因分析 */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                🚨 {t('statistics.topFailureReasons')}
              </Typography>
              <List dense>
                {data.failureReasons.map((reason, index) => (
                  <React.Fragment key={reason.reason}>
                    <ListItem>
                      <ListItemIcon>
                        <Chip 
                          label={index + 1} 
                          size="small" 
                          color="error"
                          sx={{ minWidth: '24px', height: '24px' }}
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary={t(`statistics.failureReasons.${reason.reason}`) || reason.reason}
                        secondary={`${reason.count} ${t('statistics.occurrences')} (${reason.percentage.toFixed(1)}%)`}
                      />
                    </ListItem>
                    {index < data.failureReasons.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
                {data.failureReasons.length === 0 && (
                  <ListItem>
                    <ListItemText
                      primary={t('statistics.noFailuresRecorded')}
                      secondary={`${t('statistics.greatJob')} 🎉`}
                    />
                  </ListItem>
                )}
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* 执行时长分布 */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                ⏱️ {t('statistics.durationDistribution')}
              </Typography>
              <Box>
                {data.durationDistribution.map((dist) => (
                  <Box key={dist.range} sx={{ mb: 2 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                      <Typography variant="subtitle2">
                        {dist.range}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {dist.count} tests ({dist.percentage.toFixed(1)}%)
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={dist.percentage}
                      sx={{ height: 6, borderRadius: 3 }}
                    />
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* 每日趋势 */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                📈 {t('statistics.sevenDayTrend')}
              </Typography>
              <Box>
                {data.dailyTrend.map((day) => (
                  <Box key={day.date} sx={{ mb: 1.5 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                      <Typography variant="body2">
                        {new Date(day.date + 'T00:00:00Z').toLocaleDateString('en-US', { 
                          month: 'short', 
                          day: 'numeric',
                          timeZone: 'UTC'
                        })}
                      </Typography>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="body2" color="textSecondary">
                          {day.totalTests} {t('statistics.tests')}
                        </Typography>
                        <Chip 
                          label={`${day.successRate.toFixed(0)}%`}
                          color={getSuccessRateColor(day.successRate)}
                          size="small"
                        />
                      </Box>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={day.successRate}
                      color={getSuccessRateColor(day.successRate)}
                      sx={{ height: 4, borderRadius: 2 }}
                    />
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default StatisticsCharts;
